import _extends from "@babel/runtime/helpers/extends";
/** @jsx jsx */
import React, { forwardRef, memo, useRef, useState } from 'react';
import { css, jsx } from '@emotion/react';
import { usePlatformLeafEventHandler } from '@atlaskit/analytics-next';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import __noop from '@atlaskit/ds-lib/noop';
import { N200 } from '@atlaskit/theme/colors';
import EllipsisItem from './ellipsis-item';
import { useOnRevealed } from './internal/use-on-revealed';
const defaultMaxItems = 8;
const defaultBreadcrumbsLabel = 'Breadcrumbs';
const defaultEllipsisLabel = 'Show more breadcrumbs';
const {
  toArray
} = React.Children;
const analyticsAttributes = {
  componentName: 'breadcrumbs',
  packageName: "@atlaskit/breadcrumbs",
  packageVersion: "12.0.2"
};
const noop = __noop;
const interactiveElementSelector = 'a, button, [tabindex]:not([tabindex="-1"])';
const breadcrumbStyles = css({
  display: 'flex',
  margin: "var(--ds-space-0, 0px)",
  padding: "var(--ds-space-0, 0px)",
  flexWrap: 'wrap'
});
const InnerBreadcrumbs = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    defaultExpanded = false,
    isExpanded,
    isNavigation = true,
    maxItems = defaultMaxItems,
    itemsBeforeCollapse = 1,
    itemsAfterCollapse = 1,
    children = [],
    testId,
    onExpand: providedExpanse = noop,
    analyticsContext,
    label = defaultBreadcrumbsLabel,
    ellipsisLabel = defaultEllipsisLabel
  } = props;
  const [expanded, setExpanse] = useState(defaultExpanded);
  const [isClickedBySpace, setExpansionTrigger] = useState(false);
  const wrapperRef = useRef(null);
  const isControlled = typeof isExpanded !== 'undefined';
  const isExpansionHandled = providedExpanse !== noop;
  const shouldExpand = isControlled ? isExpanded : expanded;
  const focusFirstRevealed = () => {
    if (wrapperRef.current) {
      const listItems = Array.from(wrapperRef.current.querySelectorAll('li'));
      const interactiveElements = listItems.map(li => li.querySelector(interactiveElementSelector));
      const elementToFocus = interactiveElements[itemsBeforeCollapse];
      const firstInteractiveElement = interactiveElements[0];
      if (elementToFocus) {
        elementToFocus.focus && elementToFocus.focus();
      } else if (firstInteractiveElement) {
        firstInteractiveElement.focus && firstInteractiveElement.focus();
      } else {
        wrapperRef.current.focus();
      }
    }
    setExpansionTrigger(false);
  };
  useOnRevealed(focusFirstRevealed, {
    isExpanded: shouldExpand,
    isDisabled: !isClickedBySpace
  });
  const handleExpansion = usePlatformLeafEventHandler({
    fn: (event, analyticsEvent) => {
      if (!isControlled) {
        setExpanse(expanded => !expanded);
      }
      if (isExpansionHandled && isControlled || !isControlled) {
        setExpansionTrigger(event.target === document.activeElement);
      }
      return providedExpanse(event, analyticsEvent);
    },
    action: 'expanded',
    analyticsData: analyticsContext,
    ...analyticsAttributes
  });
  const renderItemsWithEllipsis = () => {
    const allItems = childrenArray;
    // This defends against someone passing weird data, to ensure that if all
    // items would be shown anyway, we just show all items without the EllipsisItem
    if (itemsBeforeCollapse + itemsAfterCollapse >= allItems.length) {
      return allItems;
    }
    const beforeItems = allItems.slice(0, itemsBeforeCollapse);
    const afterItems = allItems.slice(allItems.length - itemsAfterCollapse, allItems.length);
    return [...beforeItems, jsx(EllipsisItem, {
      key: "ellipsis",
      testId: testId && `${testId}--breadcrumb-ellipsis`,
      onClick: handleExpansion,
      label: ellipsisLabel
    }), ...afterItems];
  };
  const childrenArray = toArray(children);
  const shouldDisplayItems = shouldExpand || maxItems && childrenArray.length <= maxItems;
  const breadcrumbsItems = shouldDisplayItems ? childrenArray : renderItemsWithEllipsis();
  const Component = isNavigation ? 'nav' : 'div';
  return jsx(Component, {
    "aria-label": label,
    ref: mergeRefs([ref, wrapperRef]),
    tabIndex: -1
  }, jsx("ol", {
    "data-testid": testId,
    css: breadcrumbStyles,
    style: {
      color: `var(--ds-text-subtlest, ${N200})`
    }
  }, breadcrumbsItems));
});
const Breadcrumbs = /*#__PURE__*/memo( /*#__PURE__*/forwardRef((props, ref) => {
  return jsx(InnerBreadcrumbs, _extends({}, props, {
    ref: ref
  }));
}));

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export default Breadcrumbs;